import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Box,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material';
import {
  Menu as MenuIcon,
  Person as AccountIcon,
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminIcon,
  Add as AddIcon,
  TrendingUp,
} from '@mui/icons-material';
import classNames from 'classnames';

//images
import profile from '../../images/main-profile.png';
import config from '../../config';

// styles
import useStyles from './styles';

import { getAssociateProfile } from '../../services/associate';

// components
import { Typography, Avatar } from '../Wrappers/Wrappers';

// context
import {
  useLayoutState,
  useLayoutDispatch,
  toggleSidebar,
} from '../../context/LayoutContext';
import {
  useManagementDispatch,
  useManagementState,
} from '../../context/ManagementContext';

import { actions } from '../../context/ManagementContext';
import { useUserDispatch, signOut } from '../../context/UserContext';
import { useAuth } from '../../context/AuthContext';
import { BRANDING } from '../../utils/branding';
import { getReadableModulesSet, hasWritePermission, getUserRole } from '../../utils/menuFilter';

export default function Header(props) {
  let classes = useStyles();
  let theme = useTheme();

  // global
  let layoutState = useLayoutState();
  let layoutDispatch = useLayoutDispatch();
  let userDispatch = useUserDispatch();
  const managementDispatch = useManagementDispatch();
  const { user: authUser } = useAuth();

  // local
  const [profileMenu, setProfileMenu] = useState(null);
  const [currentUser, setCurrentUser] = useState();
  const [userRole, setUserRole] = useState('employee');
  const [isSmall, setSmall] = useState(false);

  const [randomAvatar] = useState(`https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70)}`);

  const managementValue = useManagementState();

  useEffect(() => {
    actions.doFind(sessionStorage.getItem('user_id'))(managementDispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (config.isBackend) {
      setCurrentUser(managementValue.currentUser);
    }
  }, [managementValue]);

  useEffect(function () {
    window.addEventListener('resize', handleWindowWidthChange);
    handleWindowWidthChange();
    return function cleanup() {
      window.removeEventListener('resize', handleWindowWidthChange);
    };
  });

  // Get user role from localStorage
  useEffect(() => {
    const updateUserRole = () => {
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          setUserRole(userData.role || 'employee');
        }
      } catch (error) {
        console.log('Error getting user role:', error);
      }
    };

    // Update role on component mount
    setUserRole(getUserRole());

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'roleModules') {
        setUserRole(getUserRole());
      }
    };

    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(() => setUserRole(getUserRole()), 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const roleFromToken = authUser?.role;
    if (roleFromToken) setUserRole(roleFromToken);
  }, [authUser?.role]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getAssociateProfile();
        if (response.status && response.data) {
          setCurrentUser((prev) => ({
            ...prev,
            firstName: response.data.name?.split(' ')[0],
            avatar: [{ publicUrl: response.data.img || response.data.imgUrl }],
          }));
        }
      } catch (error) {
        console.error('Failed to fetch profile', error);
      }
    };
    fetchProfile();

    // Listen for profile updates from other components
    window.addEventListener('profileUpdated', fetchProfile);
    return () => window.removeEventListener('profileUpdated', fetchProfile);
  }, []);

  // Explicit Admin Check for Header Buttons
  const isActualAdmin = (getUserRole() || '').toLowerCase().trim() === 'admin' ||
    (getUserRole() || '').toLowerCase().trim() === 'superadmin';

  // Secondary check against AuthContext for redundancy
  const contextRole = (authUser?.role || '').toString().toLowerCase().trim();
  const isContextAdmin = contextRole === 'admin' || contextRole === 'superadmin';

  // Final composite check
  const isAdmin = isActualAdmin && isContextAdmin;

  const canWriteRoles = hasWritePermission('user-management.create-roles');

  function handleWindowWidthChange() {
    let windowWidth = window.innerWidth;
    let breakpointWidth = theme.breakpoints.values.md;
    let isSmallScreen = windowWidth < breakpointWidth;
    setSmall(isSmallScreen);
  }

  return (
    <AppBar
      position='fixed'
      className={classes.appBar}
      elevation={1}
      color='default'
    >

      <Toolbar className={classes.toolbar}>
        {/* Sidebar toggle */}
        <IconButton
          color='primary'
          onClick={() => toggleSidebar(layoutDispatch)}
          className={classNames(
            classes.headerMenuButton,
            classes.headerMenuButtonCollapse,
          )}
        >
          {(!layoutState.isSidebarOpened && isSmall) ||
            (layoutState.isSidebarOpened && !isSmall) ? (
            <ArrowBackIcon
              classes={{
                root: classNames(
                  classes.headerIcon,
                  classes.headerIconCollapse,
                ),
              }}
              sx={{ color: '#0f172a !important' }}
            />
          ) : (
            <MenuIcon
              classes={{
                root: classNames(
                  classes.headerIcon,
                  classes.headerIconCollapse,
                ),
              }}
              sx={{ color: '#0f172a !important' }}
            />
          )}
        </IconButton>



        {/* Logo / title */}
        <Box sx={{ display: isSmall ? 'none' : 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={BRANDING.logo}
            alt={`${BRANDING.name} Logo`}
            sx={{
              height: 32,
              mr: 1,
              mixBlendMode: 'multiply',
            }}
          />
          <Typography
            variant='h6'
            weight='medium'
            className={classes.logotype}
            style={{
              color: '#0f172a',
            }}
          >
            {BRANDING.name}
          </Typography>
        </Box>

        <div className={classes.grow} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: { xs: 1, sm: 1.5 },
          }}
        >
          {canWriteRoles && isAdmin ? (
            <Tooltip title='Add Role' arrow>
              <Button
                size='small'
                variant='contained'
                startIcon={<AddIcon />}
                onClick={() => props.history.push('/app/creteRoles')}
                aria-label='add-role'
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.75,
                  minHeight: 36,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                  gap: 1,
                }}
              >
                <Box component='span' sx={{ display: { xs: 'none', sm: 'inline' } }}>Add Role</Box>
              </Button>
            </Tooltip>
          ) : null}

          {/* Avatar / profile menu trigger */}
          <IconButton
            aria-haspopup='true'
            color='primary'
            className={classes.headerMenuButton}
            aria-controls='profile-menu'
            onClick={(e) => setProfileMenu(e.currentTarget)}
          >
            <Avatar
              alt={currentUser?.firstName}
              src={
                currentUser?.avatar?.length >= 1 &&
                  currentUser?.avatar[currentUser.avatar.length - 1]?.publicUrl
                  ? currentUser.avatar[currentUser.avatar.length - 1].publicUrl
                  : undefined
              }
              classes={{ root: classes.headerIcon }}
            >
              {currentUser?.firstName?.[0] || <AccountIcon />}
            </Avatar>
          </IconButton>

          {/* Greeting */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              ml: 0.5,
            }}
          >
            <Typography
              block
              style={{
                display: 'flex',
                alignItems: 'center',
                marginLeft: 0,
              }}
            >
              <div className={classes.profileLabel}>Hi,&nbsp;</div>
              <Typography weight={'bold'} className={classes.profileLabel}>
                {currentUser?.firstName}
              </Typography>
            </Typography>
          </Box>
        </Box>

        {/* Profile dropdown */}
        <Menu
          id='profile-menu'
          open={Boolean(profileMenu)}
          anchorEl={profileMenu}
          onClose={() => setProfileMenu(null)}
          className={classes.headerMenu}
          classes={{ paper: classes.profileMenu }}
          disableAutoFocusItem
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <div className={classes.profileMenuUser}>
            {/* <Typography variant='h4' weight='medium'>
              {currentUser?.firstName}
            </Typography> */}
            <Typography
              className={classes.profileMenuLink}
              component='a'
              color='primary'
              href='#'
            >
              {BRANDING.name}
            </Typography>
          </div>
          {(() => {
            const readable = getReadableModulesSet();
            const role = String(authUser?.role || '').toLowerCase();
            const skipCheck = role === 'superadmin' || !readable;

            return (
              <>
                {(skipCheck || readable.has('profile.my-profile')) && (
                  <MenuItem
                    component={Link}
                    to='/app/profile/myProfile'
                    onClick={() => setProfileMenu(null)}
                    className={classes.profileMenuItem}
                  >
                    <AccountIcon className={classes.profileMenuIcon} />
                    My Profile
                  </MenuItem>
                )}
                {/* {(skipCheck || readable.has('profile.my-business')) && (
                  <MenuItem
                    component={Link}
                    to='/app/profile/myBusiness'
                    onClick={() => setProfileMenu(null)}
                    className={classes.profileMenuItem}
                  >
                    <TrendingUp className={classes.profileMenuIcon} />
                    My Business
                  </MenuItem>
                )} */}
              </>
            );
          })()}
          <div className={classes.profileMenuUser}>
            <Typography
              className={classes.profileMenuLink}
              color='primary'
              style={{ cursor: 'pointer' }}
              onClick={() => {
                signOut(userDispatch, props.history);
                setProfileMenu(null);
              }}
            >
              Sign Out
            </Typography>
          </div>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
